import { motion } from "framer-motion";
import { Leaf, Heart, Utensils } from "lucide-react";
import { useT } from "../lib/i18n";
import { MustacheMark } from "./MustacheMark";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

const FEATURES = [
  { id: "fresh", icon: Leaf, key: "showcase.feature.1" },
  { id: "love", icon: Heart, key: "showcase.feature.2" },
  { id: "togo", icon: Utensils, key: "showcase.feature.3" },
];

export const Showcase = () => {
  const { t } = useT();
  return (
    <section
      id="showcase"
      data-testid="showcase-section"
      className="relative bg-black text-white py-24 md:py-32 overflow-hidden border-t border-gold/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-12 gap-10 md:gap-16 items-center">
        {/* Text block (left on desktop) */}
        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-15%" }}
          className="col-span-12 md:col-span-5 order-2 md:order-1"
        >
          <motion.span
            variants={fadeUp}
            className="text-[11px] tracking-[0.4em] uppercase text-gold"
          >
            {t("showcase.kicker")}
          </motion.span>

          <motion.h2
            variants={fadeUp}
            data-testid="showcase-heading"
            className="mt-5 font-display leading-[0.95] tracking-tight"
          >
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-gold">
              {t("showcase.title.1")}
            </span>
            <span
              className="block italic text-gold/95 text-5xl sm:text-6xl md:text-7xl lg:text-8xl -mt-1"
              style={{ fontFamily: '"Bodoni Moda", serif', fontStyle: "italic" }}
            >
              {t("showcase.title.2")}
            </span>
          </motion.h2>

          <motion.div
            variants={fadeUp}
            className="mt-8 flex items-center gap-3 text-gold"
          >
            <span className="h-px w-10 bg-gold/60" />
            <MustacheMark className="h-4 w-auto" color="#D4A857" />
            <span className="h-px w-10 bg-gold/60" />
          </motion.div>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-[11px] md:text-xs tracking-[0.35em] uppercase text-white/85"
          >
            {t("showcase.subtitle")}
          </motion.p>

          <motion.p
            variants={fadeUp}
            className="mt-4 font-display italic text-2xl md:text-3xl text-white/85 leading-snug max-w-md"
          >
            {t("showcase.intro")}
          </motion.p>

          {/* Badge */}
          <motion.div
            variants={fadeUp}
            data-testid="showcase-badge"
            className="mt-10 inline-flex items-center gap-4 border border-gold/40 px-6 py-4"
          >
            <MustacheMark className="h-4 w-auto" color="#D4A857" />
            <div className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-gold leading-tight">
              <div>{t("showcase.badge.1")}</div>
              <div className="italic font-display text-base normal-case tracking-normal text-gold/90 mt-1">
                {t("showcase.badge.2")}
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Image block (right on desktop) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="col-span-12 md:col-span-7 order-1 md:order-2 relative"
        >
          <div className="relative aspect-square overflow-hidden">
            <img
              src="/feature-toast.jpg"
              alt={t("showcase.subtitle")}
              data-testid="showcase-image"
              className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-[1500ms] ease-out"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-gold/15" />
            {/* gold corner accents */}
            <span className="absolute top-3 left-3 w-6 h-6 border-t border-l border-gold/60" />
            <span className="absolute top-3 right-3 w-6 h-6 border-t border-r border-gold/60" />
            <span className="absolute bottom-3 left-3 w-6 h-6 border-b border-l border-gold/60" />
            <span className="absolute bottom-3 right-3 w-6 h-6 border-b border-r border-gold/60" />
          </div>
        </motion.div>
      </div>

      {/* Bottom features */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-6xl mx-auto px-6 md:px-10 mt-20 md:mt-24"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-gold/15 ring-1 ring-gold/20">
          {FEATURES.map(({ id, icon: Icon, key }) => (
            <div
              key={id}
              data-testid={`showcase-feature-${id}`}
              className="flex flex-col items-center text-center gap-4 p-8 md:p-10 bg-black"
            >
              <Icon size={22} className="text-gold" strokeWidth={1.4} />
              <div className="text-[11px] md:text-xs tracking-[0.3em] uppercase text-white/85 leading-snug max-w-[220px]">
                {t(key)}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Showcase;
